import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { MdCategory, MdChevronRight, MdEdit, MdInventory2, MdOutlineCategory } from 'react-icons/md';

import { Txt } from '@app/components/common';
import { booleanBodyRender, TableColumn } from '@app/components/common/table';

import { SkuLookups, SkuTreeRow, TreeRowKind } from './types';

const KIND_LABEL: Record<TreeRowKind, string> = {
  category: 'Категория',
  subcategory: 'Подкатегория',
  sku: 'SKU',
};

const KindIcon = ({ kind }: { kind: TreeRowKind }) => {
  if (kind === 'category') {return <MdCategory color="var(--chakra-colors-brand-500)" />;}
  if (kind === 'subcategory') {return <MdOutlineCategory color="var(--chakra-colors-brand-400)" />;}
  return <MdInventory2 color="var(--chakra-colors-gray-500)" />;
};

export const useTreeColumns = ({
  lookups,
  onEdit,
  canChange,
}: {
  lookups: SkuLookups;
  onEdit: (row: SkuTreeRow) => void;
  canChange: boolean;
}): TableColumn<SkuTreeRow>[] => {
  return [
    {
      name: 'name',
      label: 'Наименование',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (name: string, { rowData }) => {
          const isLeaf = rowData.kind === 'sku';
          return (
            <Flex alignItems="center" gap={2} cursor={isLeaf ? 'default' : 'pointer'}>
              <KindIcon kind={rowData.kind} />
              <Txt color={isLeaf ? 'black' : 'brand.500'}>{name}</Txt>
              {!isLeaf && <MdChevronRight color="var(--chakra-colors-gray-400)" />}
            </Flex>
          );
        },
      },
    },
    {
      name: 'kind',
      label: 'Тип',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (kind: TreeRowKind) => <Txt color="gray.500">{KIND_LABEL[kind]}</Txt>,
      },
    },
    {
      name: 'sku',
      label: 'Бренд / Ед. изм.',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, { rowData }) => {
          if (rowData.kind !== 'sku' || !rowData.sku) {
            return null;
          }
          const unit = rowData.sku.unit_id ? lookups.unitsById[rowData.sku.unit_id] : undefined;
          return (
            <Box>
              <Txt color="brand.500">{rowData.sku.brand || '—'}</Txt>
              <Txt color="gray.500">{unit || '—'}</Txt>
            </Box>
          );
        },
      },
    },
    {
      name: 'is_active',
      label: 'Активен',
      options: {
        filter: false,
        sort: false,
        customBodyRender: booleanBodyRender(),
      },
    },
    ...(canChange
      ? ([
          {
            name: 'id',
            label: 'Действия',
            options: {
              filter: false,
              sort: false,
              customBodyRender: (_, { rowData }) => {
                if (rowData.kind !== 'sku') {
                  return null;
                }
                return (
                  <Flex gap={2}>
                    <Tooltip label="Редактировать">
                      <IconButton
                        name="edit"
                        aria-label="edit"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(rowData);
                        }}
                      >
                        <MdEdit />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                );
              },
            },
          },
        ] as TableColumn<SkuTreeRow>[])
      : []),
  ];
};
